/**
 * "My obligations" panel (P102) -- the per-entity regulatory
 * obligation set, surfaced on every entity detail page.
 *
 * Reads the OBLIGATION chain snapshot (sealed by the P101 deriver) and
 * lists which clauses apply to this entity, grouped by source
 * framework. A 404 (no snapshot sealed yet) renders an empty state,
 * not an error.
 *
 * `met` is the assessment overlay -- always false today (P101
 * establishes applicability, not met/open); the pill renders "open"
 * uniformly until an assessment layer lands.
 */
import { frameworkLabel } from "shared/vocab/workflowState";
import { useState } from "react";
import { formatDateTime } from "shared/time/formatDate";
import { useBankIdentity } from "shared/api/hooks/useBankIdentity";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useConfirmEquivalence,
  useDeleteEquivalence,
  useFindObligationEquivalents,
  useObligationEquivalences,
  useObligationSnapshot,
  useObligationTraceability,
} from "twin/api/hooks";
import { hasObligationChain } from "twin/entity/derivedChains";
import { EngineBusy } from "shared/components/EngineBusy";
import { GroundingNote } from "shared/components/GroundingNote";
import { EngineLanguageNote } from "shared/components/EngineLanguageNote";
import type {
  ConfirmedEquivalence,
  ControlAssessment,
  EntityKind,
  ObligationEquivalent,
  ObligationItem,
} from "shared/api/types";

const CAP_STATE_PILL: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  in_review: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  sent_back: "bg-red-100 text-red-800 border-red-200",
};

interface ObligationsPanelProps {
  kind: EntityKind;
  entityId: string;
}

/** Group obligations by source framework (falling back to jurisdiction),
 *  returning [label, items][] sorted by label, items sorted by code. */
function groupByFramework(items: ObligationItem[]): [string, ObligationItem[]][] {
  const groups = new Map<string, ObligationItem[]>();
  for (const item of items) {
    const key = item.source_framework ?? item.jurisdiction;
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  return [...groups.entries()]
    .map(([label, group]) => {
      group.sort((a, b) => a.code.localeCompare(b.code));
      return [label, group] as [string, ObligationItem[]];
    })
    .sort((a, b) => a[0].localeCompare(b[0]));
}

/** Humanise a framework/jurisdiction key e.g. "sama_csf" -> "Sama Csf". */
// F3: this title-cased mechanically, so `cbuae_outsourcing` read
// "Cbuae Outsourcing". The acronym handling lives in the shared vocab
// module beside the workflow-state catalogue -- same class of defect.
const prettyLabel = frameworkLabel;

const STATUS_PILL: Record<ControlAssessment["status"], string> = {
  compliant: "bg-emerald-100 text-emerald-900 border-emerald-200",
  partial: "bg-amber-100 text-amber-900 border-amber-200",
  non_compliant: "bg-red-100 text-red-900 border-red-200",
  no_evidence: "bg-slate-100 text-slate-600 border-slate-200",
};

/** P140: Engine finds obligations in OTHER frameworks stating the same
 *  requirement. On-demand (button-triggered) -- the Engine call is
 *  explicit, read-only, and never auto-fires on expand. */
function ConfirmedEquivalencesList({ obligationId }: { obligationId: string }) {
  const { t } = useTranslation();
  const confirmed = useObligationEquivalences(obligationId, true);
  const remove = useDeleteEquivalence(obligationId);
  const rows = confirmed.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div data-testid="equivalents-confirmed" className="mb-1">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
        {t("modules.twin.entity.obligations.equivalents.confirmedHeading")}
      </p>
      <ul className="mt-1 space-y-1">
        {rows.map((eq: ConfirmedEquivalence) => (
          <li
            data-testid="equivalent-confirmed-row"
            key={eq.link_id}
            className="rounded border border-emerald-100 bg-emerald-50/40 px-2 py-1"
          >
            <div className="flex items-baseline gap-x-2">
              <span className="font-mono text-[10px] font-semibold text-emerald-700">
                {prettyLabel(eq.source_framework ?? "")}
                {eq.citation_ref ? ` ${eq.citation_ref}` : ""}
              </span>
              <span className="text-xs text-slate-800 break-words">{eq.title}</span>
              <button
                type="button"
                data-testid={`equivalent-unconfirm-${eq.link_id}`}
                onClick={() => remove.mutate(eq.link_id)}
                disabled={remove.isPending}
                title={t("modules.twin.entity.obligations.equivalents.unconfirm")}
                className="ms-auto text-[11px] text-slate-400 hover:text-rag-red disabled:opacity-50"
              >
                ×
              </button>
            </div>
            <EquivalentPivot obligationId={eq.obligation_id} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EquivalentsBlock({ obligationId }: { obligationId: string }) {
  const { t } = useTranslation();
  const find = useFindObligationEquivalents(obligationId);
  const confirm = useConfirmEquivalence(obligationId);
  const data = find.data;

  const onConfirm = (eq: ObligationEquivalent) => {
    confirm.mutate({
      target_obligation_id: eq.id,
      confidence: eq.confidence,
      rationale: eq.rationale,
      source: "engine",
    });
  };

  return (
    <div data-testid="equivalents-block" className="pt-1">
      <ConfirmedEquivalencesList obligationId={obligationId} />

      <button
        type="button"
        data-testid="equivalents-find-button"
        onClick={() => find.mutate()}
        disabled={find.isPending}
        aria-busy={find.isPending}
        className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-[11px] font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
      >
        {find.isPending ? (
          <EngineBusy
            label={t("modules.twin.entity.obligations.equivalents.finding")}
          />
        ) : (
          t("modules.twin.entity.obligations.equivalents.find")
        )}
      </button>

      {find.isError && (
        <p data-testid="equivalents-error" className="mt-1 text-[11px] text-slate-500">
          {find.error.status === 503
            ? t("modules.twin.entity.obligations.equivalents.engineOff")
            : t("modules.twin.entity.obligations.equivalents.error")}
        </p>
      )}

      {data && data.equivalents.length === 0 && (
        <p
          data-testid="equivalents-empty"
          className="mt-1 text-[11px] italic text-slate-400"
        >
          {t("modules.twin.entity.obligations.equivalents.none")}
        </p>
      )}

      {data && data.equivalents.length > 0 && (
        <ul data-testid="equivalents-list" className="mt-1 space-y-1">
          {data.equivalents.map((eq: ObligationEquivalent) => (
            <li
              data-testid="equivalent-row"
              key={eq.id}
              className="rounded border border-indigo-100 bg-indigo-50/40 px-2 py-1"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono text-[10px] font-semibold text-indigo-700">
                  {prettyLabel(eq.source_framework ?? "")}
                  {eq.citation_ref ? ` ${eq.citation_ref}` : ""}
                </span>
                <span className="text-xs text-slate-800 break-words">{eq.title}</span>
                <span
                  data-testid="equivalent-confidence"
                  className="ms-auto text-[10px] tabular-nums text-indigo-700"
                >
                  {Math.round(eq.confidence * 100)}%
                </span>
                {eq.already_confirmed ? (
                  <span
                    data-testid="equivalent-confirmed-badge"
                    className="text-[10px] font-medium text-emerald-700"
                  >
                    {t("modules.twin.entity.obligations.equivalents.confirmedBadge")}
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid={`equivalent-confirm-${eq.id}`}
                    onClick={() => onConfirm(eq)}
                    disabled={confirm.isPending}
                    className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    {t("modules.twin.entity.obligations.equivalents.confirm")}
                  </button>
                )}
              </div>
              {eq.rationale && (
                <p className="text-[11px] text-slate-500">{eq.rationale}</p>
              )}
              <EquivalentPivot obligationId={eq.id} />
            </li>
          ))}
        </ul>
      )}

      {data && <GroundingNote grounding={data.grounding} />}
      {data && <EngineLanguageNote />}
    </div>
  );
}

/** P105: the controls implementing one obligation + their assessment,
 *  fetched lazily when the clause is expanded. */
function TraceabilityDetail({
  obligationId,
  showEquivalents = true,
}: {
  obligationId: string;
  showEquivalents?: boolean;
}) {
  const { t } = useTranslation();
  const trace = useObligationTraceability(obligationId, true);

  if (trace.isLoading) {
    return (
      <p data-testid="trace-loading" className="text-xs text-slate-500 mt-2">
        {t("modules.twin.entity.obligations.trace.loading")}
      </p>
    );
  }
  // 403 = no framework module access; degrade quietly.
  if (trace.isError) {
    const key =
      trace.error.status === 403
        ? "modules.twin.entity.obligations.trace.forbidden"
        : "modules.twin.entity.obligations.trace.error";
    return (
      <p data-testid="trace-error" className="text-xs text-slate-500 mt-2">
        {t(key)}
      </p>
    );
  }
  const data = trace.data;
  if (!data) return null;
  const s = data.summary;

  return (
    <div data-testid="trace-detail" className="mt-2 space-y-2">
      {/* P141: the Engine-enriched requirement statement, when present --
       * what this obligation actually requires, beyond the subdomain name. */}
      {data.obligation.engine_summary && (
        <p
          data-testid="obligation-engine-summary"
          className="rounded border-s-2 border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-slate-700"
        >
          {data.obligation.engine_summary}
        </p>
      )}
      <p className="text-[11px] text-slate-500">
        {t("modules.twin.entity.obligations.trace.summary", {
          compliant: s.compliant,
          count: s.control_count,
        })}
        {s.unassessed > 0
          ? ` · ${t("modules.twin.entity.obligations.trace.unassessed", {
              count: s.unassessed,
            })}`
          : ""}
      </p>
      <ul className="space-y-1">
        {data.controls.map((c) => (
          <li
            data-testid="trace-control"
            key={c.id}
            className="flex flex-wrap items-baseline gap-x-2 rounded border border-slate-100 bg-slate-50 px-2 py-1"
          >
            <span className="font-mono text-[10px] font-semibold text-slate-500">
              {c.control_natural_id}
            </span>
            <span className="text-xs text-slate-700 break-words">
              {c.subdomain_name}
            </span>
            <span
              data-testid="trace-control-status"
              data-status={c.assessment?.status ?? "unassessed"}
              className={`ml-auto inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                c.assessment
                  ? STATUS_PILL[c.assessment.status]
                  : "bg-white text-slate-400 border-slate-200"
              }`}
            >
              {c.assessment
                ? t(
                    `modules.twin.entity.obligations.trace.status.${c.assessment.status}`,
                  )
                : t("modules.twin.entity.obligations.trace.status.unassessed")}
            </span>
          </li>
        ))}
      </ul>

      {/* P108.2: the CAP leg -- CAPs remediating the controls behind
       * this obligation. Empty state reads as deliberate (no CAPs
       * linked yet), not broken. */}
      <div data-testid="trace-caps" className="pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {t("modules.twin.entity.obligations.trace.capsHeading")}
        </p>
        {data.remediating_caps.length === 0 ? (
          <p
            data-testid="trace-caps-empty"
            className="text-[11px] italic text-slate-400"
          >
            {t("modules.twin.entity.obligations.trace.capsEmpty")}
          </p>
        ) : (
          <>
            <p className="text-[11px] text-slate-500">
              {t("modules.twin.entity.obligations.trace.capsInFlight", {
                count: s.open_cap_count,
              })}
            </p>
            <ul className="mt-1 space-y-1">
              {data.remediating_caps.map((cap) => (
                <li
                  data-testid="trace-cap"
                  key={cap.id}
                  className="flex flex-wrap items-baseline gap-x-2 rounded border border-slate-100 bg-slate-50 px-2 py-1"
                >
                  <Link
                    to={`/framework/caps/${cap.id}`}
                    className="text-xs text-brand-brassDark hover:underline break-words"
                  >
                    {cap.title}
                  </Link>
                  <span className="font-mono text-[10px] text-slate-400">
                    {cap.letter_ref}
                  </span>
                  <span
                    data-testid="trace-cap-state"
                    data-state={cap.state}
                    className={`ml-auto inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      CAP_STATE_PILL[cap.state] ??
                      "bg-white text-slate-400 border-slate-200"
                    }`}
                  >
                    {cap.state}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* P140 reverse-pivot: suppressed when this detail IS an equivalent's
       * own traceability, so we don't recurse into equivalents-of-equivalents. */}
      {showEquivalents && <EquivalentsBlock obligationId={obligationId} />}
    </div>
  );
}

/** P140 reverse-pivot: from a cross-framework equivalent, jump to ITS OWN
 *  implementing controls + CAPs (the equivalent obligation's traceability),
 *  so "we're also covered under framework X" is one click to the evidence.
 *  showEquivalents=false on the nested detail prevents infinite nesting. */
function EquivalentPivot({ obligationId }: { obligationId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1 w-full">
      <button
        type="button"
        data-testid={`equivalent-pivot-${obligationId}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-[10px] font-medium text-indigo-700 hover:underline"
      >
        {open
          ? t("modules.twin.entity.obligations.equivalents.hideControls")
          : t("modules.twin.entity.obligations.equivalents.viewControls")}
      </button>
      {open && (
        <div className="ms-2 border-s border-indigo-100 ps-2">
          <TraceabilityDetail obligationId={obligationId} showEquivalents={false} />
        </div>
      )}
    </div>
  );
}

function ObligationRow({ item }: { item: ObligationItem }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <li
      data-testid="obligation-clause"
      className="rounded-md border border-slate-200 bg-white p-3"
    >
      <button
        type="button"
        data-testid="obligation-clause-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-baseline gap-x-2 text-start"
      >
        <span aria-hidden className="marsad-caret text-[10px] text-slate-400">
          {open ? "▾" : "▸"}
        </span>
        {item.citation_ref && (
          <span className="font-mono text-[11px] font-semibold text-brand-brassDark">
            {item.citation_ref}
          </span>
        )}
        <span className="font-medium text-slate-900 break-words">{item.title}</span>
        <span
          data-testid="obligation-status"
          data-met={item.met ? "true" : "false"}
          className={`ml-auto inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            item.met
              ? "bg-emerald-100 text-emerald-900 border-emerald-200"
              : "bg-slate-100 text-slate-700 border-slate-200"
          }`}
        >
          {item.met
            ? t("modules.twin.entity.obligations.met")
            : t("modules.twin.entity.obligations.open")}
        </span>
      </button>
      {open && <TraceabilityDetail obligationId={item.id} />}
    </li>
  );
}

export function ObligationsPanel({ kind, entityId }: ObligationsPanelProps) {
  const { t, i18n } = useTranslation();
  // Round 9 E9 -- the deriver narrows the catalogue for an IBS, a
  // service and a capability. A vendor is not one of those, so the page
  // asked for a snapshot that is never sealed and rendered its 404 as
  // "nothing yet", which reads as a gap in the data rather than as the
  // shape of the model.
  const applies = hasObligationChain(kind);
  const snapshot = useObligationSnapshot(kind, entityId, applies);
  // G9 -- the identity, readable by every seat; the profile is admin-only.
  const bank = useBankIdentity();
  const [showOthers, setShowOthers] = useState(false);

  const heading = (
    <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
      {t("modules.twin.entity.obligations.heading")}
    </h2>
  );

  if (!applies) {
    return (
      <section data-testid="entity-obligations">
        {heading}
        <p data-testid="obligations-not-derived" className="text-sm text-ink-2">
          {t("modules.twin.entity.obligations.notDerivedForKind")}
        </p>
      </section>
    );
  }

  if (snapshot.isLoading) {
    return (
      <section data-testid="entity-obligations">
        {heading}
        <p data-testid="obligations-loading" className="text-sm text-slate-500">
          {t("modules.twin.entity.obligations.loading")}
        </p>
      </section>
    );
  }

  // 404 -> no snapshot sealed yet: an expected empty state.
  if (snapshot.isError && snapshot.error.status === 404) {
    return (
      <section data-testid="entity-obligations">
        {heading}
        <p
          data-testid="obligations-empty"
          className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3"
        >
          {t("modules.twin.entity.obligations.none")}
        </p>
      </section>
    );
  }

  if (snapshot.isError) {
    return (
      <section data-testid="entity-obligations">
        {heading}
        <p
          data-testid="obligations-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
        >
          {snapshot.error.detail}
        </p>
      </section>
    );
  }

  const data = snapshot.data;
  if (!data) return null;

  if (data.obligations.length === 0) {
    return (
      <section data-testid="entity-obligations">
        {heading}
        <p
          data-testid="obligations-empty"
          className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3"
        >
          {t("modules.twin.entity.obligations.none")}
        </p>
      </section>
    );
  }

  const groups = groupByFramework(data.obligations);
  // F3 -- this listed 170 obligations from FOUR regulators on a Saudi
  // bank's service, in one 11,000-pixel scroll. MARSAD speaking three
  // jurisdictions is the product; making a business user read all
  // three to find their own is not. The bank's own regime leads, and
  // the rest are named and counted rather than hidden -- an EU
  // obligation quietly dropped would be worse than one scrolled past.
  const home = bank.data?.regime_code ?? null;
  const isHome = (label: string) =>
    home === null || label.toLowerCase().startsWith(home.toLowerCase());
  const homeGroups = groups.filter(([label]) => isHome(label));
  const otherGroups = groups.filter(([label]) => !isHome(label));
  const otherCount = otherGroups.reduce((n, [, items]) => n + items.length, 0);
  const shown = showOthers ? [...homeGroups, ...otherGroups] : homeGroups;

  return (
    <section id="obligations" data-testid="entity-obligations">
      <div className="flex items-baseline justify-between mb-2">
        {heading}
        <span
          data-testid="obligations-count"
          className="text-[11px] font-semibold text-slate-500 tabular-nums"
        >
          {t("modules.twin.entity.obligations.count", {
            count: data.obligation_count,
          })}
        </span>
      </div>

      {/* Round 10 F3 -- what that number means on a capability.
          The deriver narrows the catalogue per IBS (by outcome
          category and sovereignty) and per service (by
          classification); a capability gets the WHOLE set. So every
          capability in the estate asserts the same figure, which is
          true and tells a reader nothing -- the degenerate
          distribution our own supervisory checks exist to flag,
          in our own output. The number stays; the sentence says
          what it is. */}
      {kind === "capability" && (
        <p
          data-testid="obligations-universal-note"
          className="mb-2 text-[11px] italic text-slate-500"
        >
          {t("modules.twin.entity.obligations.universalForCapability")}
        </p>
      )}

      {/* D7 -- every clause of every group rendered at once, which is
          most of an 11,467px page. A business reader wants to know how
          many apply and from where; the text of 38 clauses is a
          reference they open when they need it, not a wall they scroll
          past to reach the rest of the page. */}
      <div className="space-y-2">
        {shown.map(([label, items]) => (
          <details
            key={label}
            data-testid="obligations-group"
            data-framework={label}
            className="rounded border border-slate-200 bg-white px-3 py-2"
          >
            <summary className="cursor-pointer list-none text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {prettyLabel(label)}{" "}
              <span className="tabular-nums text-slate-400">
                {t("modules.twin.entity.obligations.clauseCount", {
                  count: items.length,
                })}
              </span>
            </summary>
            <ul className="mt-2 space-y-2">
              {items.map((item) => (
                <ObligationRow key={item.id} item={item} />
              ))}
            </ul>
          </details>
        ))}
      </div>

      {otherGroups.length > 0 && (
        <button
          type="button"
          data-testid="obligations-other-jurisdictions"
          onClick={() => setShowOthers((v) => !v)}
          className="mt-3 text-[11px] font-medium text-brand-brassDark hover:underline"
        >
          {showOthers
            ? t("modules.twin.entity.obligations.hideOthers")
            : t("modules.twin.entity.obligations.showOthers", {
                count: otherCount,
                regimes: otherGroups.map(([l]) => prettyLabel(l)).join(", "),
              })}
        </button>
      )}

      <p className="mt-2 text-[11px] text-slate-400">
        {t("modules.twin.entity.obligations.asOf", {
          when: formatDateTime(data.captured_at, i18n.language),
        })}
      </p>
    </section>
  );
}
